import { FormattedMessage, useIntl, IntlShape } from 'react-intl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';

import InfoIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';

import { red, orange, green } from '@mui/material/colors';

import { useState } from 'react';
import SeverityIcon from '~/components/SeverityIcon';
import { useTurnIssues } from '~/hooks/api/useTurnIssues';
import { PublisherResource, TurnPlanningIssueResource } from '~/generated';
import React from 'react';
import messages from './messages';

const publisherRelatedIssues = [
  'ConsecutiveAssignmentsOnDifferentRoutes',
  'ApplicationForMultipleShiftsNotRespected',
  'SeparatedFromPartner',
  'RoutePreferenceNotRespected',
  'DoubleShiftAssigned',
  'InvalidTurnLeader',
  'NonActivePublisher',
  'AssignmentRestrictionViolated',
  'AssignedToMultipleShiftsOnSameDay',
];
const getPublisherName = (publisher: { firstName: string; lastName: string }) =>
  `${publisher.firstName} ${publisher.lastName}`;

export const getPublisherNamesFromIssue = (
  issue: TurnPlanningIssueResource
) => {
  const publisherNames: string[] = [];

  if (publisherRelatedIssues.includes(issue.type || '')) {
    if ((issue.relatedAssignments?.length ?? 0) > 0) {
      issue.relatedAssignments?.forEach((assignment) => {
        if (!(assignment.publisher as PublisherResource).firstName) return;
        publisherNames.push(
          getPublisherName(assignment.publisher as PublisherResource)
        );
      });
    }
    if ((issue.relatedApplications?.length ?? 0) > 0) {
      issue.relatedApplications?.forEach((application) => {
        if (!(application.publisher as PublisherResource).firstName) return;
        publisherNames.push(
          getPublisherName(application.publisher as PublisherResource)
        );
      });
    }
  }

  return [...new Set(publisherNames)];
};

const getLabel = (
  isLoading: boolean,
  issues: TurnPlanningIssueResource[],
  intl: IntlShape
) => {
  if (isLoading) return intl.formatMessage(messages.issuesFetching);
  return issues.length > 0
    ? intl.formatMessage(messages.showIssues, {
        issueCount: issues.length,
      })
    : intl.formatMessage(messages.noIssues);
};

const getIcon = (isLoading: boolean, issues: TurnPlanningIssueResource[]) => {
  if (isLoading) return <InfoIcon />;
  if (issues.length === 0) return <CheckIcon style={{ color: green[300] }} />;
  return issues.filter((issue) => issue.severity === 'Error').length > 0 ? (
    <ErrorIcon style={{ color: red[500] }} />
  ) : (
    <InfoIcon style={{ color: orange[500] }} />
  );
};

type TurnIssuesButtonProps = {
  turnId: number;
};

export const TurnIssuesButton = (props: TurnIssuesButtonProps) => {
  const { data: issues, isLoading } = useTurnIssues({
    id: props.turnId,
    expand: ['relatedAssignments.publisher', 'relatedApplications.publisher'],
  });
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Button
        onClick={() =>
          !isLoading && issues && issues.length > 0 && setOpen(true)
        }
        startIcon={issues ? getIcon(isLoading, issues) : null}
        disabled={isLoading || (issues && issues.length === 0)}
        data-test="show-issues-button"
      >
        {issues ? getLabel(isLoading, issues, intl) : null}
      </Button>
      <Dialog onClose={() => setOpen(false)} open={open}>
        <DialogTitle>
          {intl.formatMessage(messages.showIssues, {
            issueCount: issues?.length,
          })}
        </DialogTitle>
        <DialogContent>
          <List>
            {issues?.map((issue, index) => (
              <ListItem key={index} style={{ paddingLeft: 0 }}>
                <ListItemText
                  primary={
                    <FormattedMessage
                      // @ts-ignore
                      {...messages[issue.type]}
                      values={{
                        publisherName: (
                          <b>{getPublisherNamesFromIssue(issue).join(' & ')}</b>
                        ),
                      }}
                    />
                  }
                />
                <ListItemSecondaryAction>
                  {issue.severity ? (
                    <SeverityIcon
                      severity={
                        issue.severity as React.ComponentProps<
                          typeof SeverityIcon
                        >['severity']
                      }
                    />
                  ) : null}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setOpen(false)}>
            {intl.formatMessage(messages.ok)}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TurnIssuesButton;
